import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUser, selectRole } from '../store/slices/authSlice';
import toast from 'react-hot-toast';

// ── useDebounce ───────────────────────────────────────
export const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// ── useQuery — 
export const useQuery = (fetchFn, deps = [], options = {}) => {
  const { enabled = true, onSuccess, onError } = options;
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error,   setError]   = useState(null);
  const mounted = useRef(true);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFn();
      if (mounted.current) {
        const payload = res?.data?.data ?? res?.data ?? res ?? null;
        setData(payload);
        onSuccess?.(payload);
      }
    } catch (e) {
      if (e?.response?.status === 304) {
        if (mounted.current) {
          const payload = e.response?.data?.data ?? e.response?.data ?? null;
          setData(payload);
          onSuccess?.(payload);
        }
        return;
      }
      if (mounted.current) {
        setError(e.response?.data?.message || e.response?.data?.msg || e.message);
        onError?.(e);
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, deps); 

  useEffect(() => {
    mounted.current = true;
    fetch();
    return () => { mounted.current = false; };
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

// ── useMutation —
export const useMutation = (mutateFn, options = {}) => {
  const { onSuccess, onError, successMsg, errorMsg } = options;
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const mutate = async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const res = await mutateFn(...args);
      if (successMsg) toast.success(successMsg);
      onSuccess?.(res?.data?.data ?? res?.data, args[0]);
      return res?.data?.data ?? res?.data;
    } catch (e) {
      // Don't re-throw — prevents unhandled rejection & CRA error overlay
      const msg = e.response?.data?.message || e.response?.data?.msg || e.message || errorMsg || 'Something went wrong';
      setError(msg);
      if (errorMsg !== false) toast.error(msg);
      onError?.(e);
      // Return null instead of throwing so callers don't need try/catch
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { mutate, loading, error };
};

// ── usePagination ─────────────────────────────────────
export const usePagination = (initial = 1) => {
  const [page,  setPage]  = useState(initial);
  const [limit, setLimit] = useState(20);
  const reset = () => setPage(1);
  return { page, limit, setPage, setLimit, reset };
};

// ── usePermission ─────────────────────────────────────
const ROLE_HIERARCHY = {
  super_admin   : 100,
  admin         : 80,
  moderator     : 60,
  business_owner: 40,
  business_staff: 20,
  customer      : 10,
};

export const usePermission = () => {
  const role = useSelector(selectRole);
  const can = useCallback((requiredRole) => {
    return (ROLE_HIERARCHY[role] || 0) >= (ROLE_HIERARCHY[requiredRole] || 0);
  }, [role]);

  const isRole = useCallback((...roles) => roles.includes(role), [role]);
  const isAdmin    = isRole('super_admin', 'admin', 'moderator');
  const isBusiness = isRole('business_owner', 'business_staff');
  const isSuperAdmin = isRole('super_admin');

  return { can, isRole, isAdmin, isBusiness, isSuperAdmin, role };
};

// ── useConfirm ────────────────────────────────────────
export const useConfirm = () => {
  const [state, setState] = useState({ open: false, resolve: null, props: {} });

  const confirm = useCallback((props = {}) => new Promise((resolve) => {
    setState({ open: true, resolve, props });
  }), []);

  const handleClose   = () => { state.resolve?.(false); setState({ open: false, resolve: null, props: {} }); };
  const handleConfirm = () => { state.resolve?.(true);  setState({ open: false, resolve: null, props: {} }); };

  return { confirm, dialogProps: { ...state.props, open: state.open, onClose: handleClose, onConfirm: handleConfirm } };
};

// ── useLocalStorage ───────────────────────────────────
export const useLocalStorage = (key, initial) => {
  const [value, setValue] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; }
    catch { return initial; }
  });
  const set = (v) => { setValue(v); localStorage.setItem(key, JSON.stringify(v)); };
  return [value, set];
};